// File: clientapp/src/components/PageLayout.js
import React from 'react';

import { Container, Box, Typography, IconButton, Breadcrumbs, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Props:
 * - breadcrumb: array of { label: string, to?: string } to display as breadcrumb links
 * - showBack: boolean to show/hide a back button
 * - onBack: callback when back is clicked
 * - showClose: boolean to show/hide a close (X) button
 * - onClose: callback when close is clicked
 * - icon: a JSX element for the icon
 * - title: string for the main title
 * - subTitle: string for the secondary text
 * - actionIcons: optional array of { icon: JSX.Element, onClick: function } for extra icons on the right
 * - children: page content
 */
export default function PageLayout({
    breadcrumb = [],
    showBack = false,
    onBack,
    showClose = false,
    onClose,
    icon,
    title,
    subTitle,
    actionIcons = [],
    children
}) {

    return (
        <Container sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Top row: breadcrumb on the left, optional back or close on the right */}
            <Container maxWidth="md" sx={{ pt: 2 }}>
                {/* Breadcrumb */}
                <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }} // ensures it uses available space
                >
                    {breadcrumb.map((item, idx) => {
                        
                        // Use SPA navigation to avoid full page reloads (preserves session/state)
                        return item.to ? (
                            <Link
                                key={idx}
                                component={RouterLink}
                                to={item.to}
                                color="inherit"
                                underline="hover"
                            >
                                {item.label}
                            </Link>
                        ) : (
                            <Typography key={idx} color="text.primary">
                                {item.label}
                            </Typography>
                        );

                    })}
                </Breadcrumbs>

                {/* Right side: optional back or close buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {showBack && (
                        <IconButton aria-label="Back" onClick={onBack} sx={{ mr: showClose ? 1 : 0 }}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    {showClose && (
                        <IconButton aria-label="Close" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            

            {/* Page header row: icon, title, subTitle, optional action icons on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Icon */}
                    {icon && <Box sx={{ mt: 0.5 }}>{icon}</Box>}
                    {/* Title + SubTitle */}
                    <Box>
                        {title && (
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: subTitle ? 0.5 : 0 }}>
                                {title}
                            </Typography>
                        )}
                        {subTitle && (
                            <Typography variant="body2" color="text.secondary">
                                {subTitle}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Optional action icons on the right */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {actionIcons.map((action, idx) => (
                        <IconButton key={idx} onClick={action.onClick} aria-label={`action-${idx}`}>
                            {action.icon}
                        </IconButton>
                    ))}
                </Box>
            </Box>
            </Container>

            {/* Page content */}
            <Box>{children}</Box>
        </Container>
    );
}
